import { makeStyles } from 'tss-react/mui';
import Skeleton from '@mui/material/Skeleton';
import {
  ComponentType,
  IframeHTMLAttributes,
  useCallback,
  useMemo,
  useState,
} from 'react';
import { IFrameProps } from 'shared/types';

type VideoEmbedProps = {
  embedUrl: string;
  IFrameComponent: ComponentType<
    IFrameProps & IframeHTMLAttributes<HTMLIFrameElement>
  >;
};

const SKELETON_ANIMATION = 'wave';

const EmbedLoadingStatus = {
  LOADING: 0,
  SUCCESS: 1,
  ERROR: 2,
};

const useStyles = makeStyles()((theme) => ({
  videoEmbed: {
    width: '100%',
    height: '100%',
    aspectRatio: '16 / 9',
    minHeight: 272, // ~ (GEO_EVENT_DRAWER_WIDTH - 32) / (16 / 9) - scroll bar width
    [theme.breakpoints.down('tablet')]: {
      minHeight: Math.floor((window.innerWidth - 32) / (16 / 9)),
    },
    '& iframe': {
      border: 'none',
      height: 'inherit',
      overflow: 'hidden',
      width: 'inherit',
    },
  },
}));

export const VideoEmbed = (props: VideoEmbedProps): JSX.Element => {
  const { embedUrl, IFrameComponent } = props;
  const { classes } = useStyles();
  const [readyStatus, setReadyStatus] = useState(EmbedLoadingStatus.LOADING);
  const [containerWidth, setContainerWidth] = useState<number | null>(null);

  const embedDisplay =
    readyStatus === EmbedLoadingStatus.LOADING ? 'none' : 'inherit';

  // Measured once on mount rather than observed so we don't resize and interrupt playback.
  const measureContainer = useCallback((node: HTMLDivElement | null) => {
    if (node) {
      setContainerWidth(Math.round(node.getBoundingClientRect().width));
    }
  }, []);

  const iframe = useMemo(
    () =>
      containerWidth === null ? null : (
        <IFrameComponent
          src={embedUrl}
          containerWidth={containerWidth}
          style={{ display: embedDisplay }}
          onLoad={() => setReadyStatus(EmbedLoadingStatus.SUCCESS)}
          onError={() => setReadyStatus(EmbedLoadingStatus.ERROR)}
        />
      ),
    [IFrameComponent, embedUrl, embedDisplay, containerWidth],
  );

  return (
    <div className={classes.videoEmbed} ref={measureContainer}>
      {readyStatus === EmbedLoadingStatus.LOADING && (
        <Skeleton
          animation={SKELETON_ANIMATION}
          variant="rectangular"
          height="100%"
        />
      )}
      {iframe}
    </div>
  );
};
